import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, ClipboardCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { api, formatApiErrorDetail } from "@/lib/api";
import { EmptyState, Spinner } from "./AdminShared";

const STATUS_STYLE = {
  new: "bg-blue-100 text-blue-800", reviewed: "bg-amber-100 text-amber-800",
  approved: "bg-emerald-100 text-emerald-800", declined: "bg-red-100 text-red-800",
};
const HIDDEN = new Set(["id", "_id", "status", "admin_notes", "reviewed_at", "created_at"]);
const REVIEWABLE = new Set(["applications", "volunteers", "sponsors"]);

const Row = ({ row, name, onChange, onDelete }) => {
  const [notes, setNotes] = useState(row.admin_notes || "");
  const [open, setOpen] = useState(false);
  const reviewable = REVIEWABLE.has(name);

  const update = async (status) => {
    try {
      await api.patch(`/admin/collection/${name}/${row.id}`, { status, admin_notes: notes });
      toast.success("Status updated"); onChange();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="p-5 hover:bg-slate-50" data-testid={`submission-row-${row.id}`}>
      <div className="flex items-start gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm">
            {Object.entries(row).filter(([k]) => !HIDDEN.has(k)).map(([k, v]) => (
              <span key={k}><span className="font-semibold text-navy-800 capitalize">{k.replace(/_/g, " ")}:</span>{" "}
                <span className="text-slate-600">{Array.isArray(v) ? v.join(", ") : String(v)}</span></span>
            ))}
          </div>
          <p className="text-xs text-slate-400 mt-1">Received {row.created_at?.slice(0, 10)}{row.reviewed_at && ` · Reviewed ${row.reviewed_at.slice(0, 10)}`}</p>
        </div>
        {reviewable && (
          <>
            <Badge className={`${STATUS_STYLE[row.status] || STATUS_STYLE.new} capitalize border-0`} data-testid={`submission-status-${row.id}`}>{row.status || "new"}</Badge>
            <Button size="icon" variant="ghost" onClick={() => setOpen(!open)} data-testid={`submission-review-${row.id}`}><ClipboardCheck className="h-4 w-4" /></Button>
          </>
        )}
        <Button size="icon" variant="ghost" className="text-red-600" onClick={onDelete} data-testid={`submission-delete-${row.id}`}><Trash2 className="h-4 w-4" /></Button>
      </div>
      {open && reviewable && (
        <div className="mt-4 grid sm:grid-cols-[1fr_auto] gap-3 items-end bg-slate-50 rounded-xl p-4 border border-slate-200">
          <Textarea rows={2} placeholder="Admin notes (internal)" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid={`submission-notes-${row.id}`} />
          <Select value={row.status || "new"} onValueChange={update}>
            <SelectTrigger className="w-40" data-testid={`submission-status-select-${row.id}`}><SelectValue /></SelectTrigger>
            <SelectContent>
              {["new", "reviewed", "approved", "declined"].map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      )}
    </div>
  );
};

export const SubmissionsManager = ({ name }) => {
  const [rows, setRows] = useState(null);
  const [filter, setFilter] = useState("all");

  const load = () => api.get(`/admin/collection/${name}`).then((r) => setRows(r.data)).catch(() => toast.error("Failed to load"));
  useEffect(() => { setRows(null); load(); }, [name]); // eslint-disable-line

  const remove = async (row) => {
    if (!window.confirm("Delete this submission?")) return;
    await api.delete(`/admin/collection/${name}/${row.id}`); toast.success("Deleted"); load();
  };

  const visible = (rows || []).filter((r) => filter === "all" || (r.status || "new") === filter);

  return (
    <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden" data-testid={`submissions-manager-${name}`}>
      {REVIEWABLE.has(name) && (
        <div className="p-4 border-b border-slate-100 flex gap-2 flex-wrap">
          {["all", "new", "reviewed", "approved", "declined"].map((s) => (
            <button key={s} onClick={() => setFilter(s)} data-testid={`submission-filter-${s}`}
              className={`px-4 py-1.5 rounded-full text-xs font-semibold capitalize transition-colors ${filter === s ? "bg-navy-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>{s}</button>
          ))}
        </div>
      )}
      {!rows ? <Spinner /> : visible.length === 0 ? <EmptyState text="No submissions here." /> : (
        <div className="divide-y divide-slate-100">
          {visible.map((row) => <Row key={row.id} row={row} name={name} onChange={load} onDelete={() => remove(row)} />)}
        </div>
      )}
    </div>
  );
};
